/**
 * Resume data structured for intelligent Q&A matching
 * Updated to match Ultimate Resume - Full-Stack Software Engineer
 * Each entry contains:
 * - keywords: Terms that might appear in questions
 * - patterns: Regex patterns for matching
 * - answer: Response text or function
 * - category: Grouping for follow-up suggestions
 * - suggestions: Follow-up question suggestions
 */

export const resumeQA = [
  // CURRENT ROLE
  {
    keywords: [
      "current",
      "now",
      "currently",
      "working",
      "job",
      "role",
      "position",
      "doing",
    ],
    patterns: [
      /what.*doing/i,
      /current.*role/i,
      /where.*work/i,
      /working.*now/i,
    ],
    questionType: "what",
    category: "experience",
    answer:
      "Right now, I'm working as a Software Developer at Progress Solutions Inc. I work remotely from New York. I build full-stack applications with React.js, Vue.js, and TypeScript on the frontend, and Node.js/Python (FastAPI) on the backend. Most of my day goes into building real-time data visualization dashboards that process over 1 million data points and serve 50,000+ daily users. I was promoted from intern to full-time in July 2025.",
    suggestions: [
      "What do you work on daily?",
      "What's your tech stack?",
      "Tell me about your projects",
    ],
  },

  // DAILY WORK
  {
    keywords: [
      "daily",
      "day to day",
      "typical day",
      "responsibilities",
      "work on",
    ],
    patterns: [/what.*do.*daily/i, /day.*to.*day/i, /typical.*day/i],
    questionType: "what",
    category: "experience",
    answer:
      "My typical day involves full-stack development. On the frontend, I build component libraries with React and Vue using TypeScript, Storybook for documentation, and TanStack Query for server state management. I work a lot on performance optimization — for example, I brought dashboard rendering time from 2-3 seconds down to under 500ms. On the backend, I build APIs with Node.js, Express, and Python FastAPI. I also work with Kafka for real-time data streaming and use Docker and Kubernetes for deployments. Everything runs through CI/CD pipelines with automated testing using Jest, Playwright, and Cypress.",
    suggestions: [
      "What technologies do you use?",
      "How did you optimize performance?",
      "Tell me about your full-stack work",
    ],
  },

  // FULL-STACK EXPERTISE
  {
    keywords: [
      "fullstack",
      "full-stack",
      "full stack",
      "both",
      "frontend and backend",
    ],
    patterns: [/full.*stack/i, /frontend.*backend/i, /backend.*frontend/i],
    questionType: "what",
    category: "skills",
    answer:
      "I'm a full-stack software engineer with frontend-heavy expertise. On the frontend, I specialize in React.js and Vue.js with TypeScript, building reusable component libraries documented with Storybook. I use Redux Toolkit and Pinia for state management, TanStack Query for server state, and create custom hooks and composables for reusability. On the backend, I work with Node.js, Express, Python (FastAPI and Django), GraphQL, and build RESTful APIs. I'm experienced with PostgreSQL, MongoDB, Redis caching, and real-time systems using WebSockets and Kafka. I deploy everything using Docker, Kubernetes, and AWS, with comprehensive CI/CD pipelines.",
    suggestions: [
      "What's your React experience?",
      "Tell me about your backend skills",
      "What about data visualization?",
    ],
  },

  // REACT EXPERTISE
  {
    keywords: ["react", "reactjs", "next", "nextjs", "redux"],
    patterns: [/react/i, /next/i, /redux/i],
    questionType: "what",
    category: "skills",
    answer:
      "I have strong React.js experience. I've built production apps serving 15 million+ annual visitors. I work with modern React patterns — custom hooks, compound components, React.memo, useMemo, and useCallback for performance optimization. I use Redux Toolkit for state management, TanStack Query (React Query) for server state, and Next.js for SSR/SSG. I document components with Storybook and test everything with Jest, React Testing Library, and Playwright. I've optimized apps from 8-second load times down to 2.5 seconds and consistently achieve 90+ Lighthouse scores.",
    suggestions: [
      "What's your Vue.js experience?",
      "Tell me about performance optimization",
      "What projects did you build?",
    ],
  },

  // VUE EXPERTISE
  {
    keywords: ["vue", "vuejs", "vue.js", "vue 3", "nuxt", "pinia"],
    patterns: [/vue/i, /nuxt/i, /pinia/i, /composition.*api/i],
    questionType: "what",
    category: "skills",
    answer:
      "Vue.js is one of my core specializations. I've built 14+ production apps with Vue 3 using Composition API, Pinia for state management, and custom composables for reusability. I work with Nuxt.js for SSR/SSG, and I'm very comfortable with Vue's reactivity system. I've done extensive real-time data visualization work with Vue using Plotly.js and D3.js. Performance is a big focus — I've reduced code duplication, improved UI responsiveness, and optimized bundle sizes across multiple Vue applications.",
    suggestions: [
      "What projects did you build with Vue?",
      "Tell me about your state management experience",
      "What's your React expertise?",
    ],
  },

  // TECH STACK
  {
    keywords: [
      "skills",
      "technologies",
      "tech stack",
      "tools",
      "know",
      "expertise",
      "proficient",
    ],
    patterns: [/what.*skills/i, /tech.*stack/i, /technologies/i, /good.*at/i],
    questionType: "what",
    category: "skills",
    answer:
      "My tech stack is full-stack with frontend specialization. Frontend: React.js, Vue.js, Next.js, Nuxt.js, TypeScript, Redux Toolkit, Pinia, TanStack Query, Storybook. Component patterns: custom hooks, custom composables, compound components. Backend: Node.js, Express, Python (FastAPI, Django), GraphQL, RESTful APIs. Databases: PostgreSQL, MongoDB, Redis. Real-time: WebSockets, Socket.io, Kafka. DevOps: Docker, Kubernetes, AWS (Lambda, S3, CloudFront), CI/CD with GitHub Actions. Testing: Jest, Playwright, Cypress, React Testing Library, Vue Test Utils. Design tools: Figma, Tailwind CSS, Material-UI. Project management: Jira, Agile/Scrum.",
    suggestions: [
      "What's your React experience?",
      "Tell me about your Vue.js skills",
      "What about data visualization?",
    ],
  },

  // MODERN TOOLING
  {
    keywords: [
      "storybook",
      "tanstack",
      "react query",
      "playwright",
      "figma",
      "jira",
    ],
    patterns: [
      /storybook/i,
      /tanstack/i,
      /react.*query/i,
      /playwright/i,
      /figma/i,
      /jira/i,
    ],
    questionType: "what",
    category: "skills",
    answer:
      "I work with modern development tools daily. I use Storybook to document component libraries — it's been a game-changer for team collaboration and component reusability. TanStack Query (React Query) handles all my server state management with intelligent caching and background refetching. For testing, Playwright is my go-to for E2E tests alongside Jest and Cypress. I collaborate with designers using Figma for design handoffs and work in Agile/Scrum environments using Jira for sprint planning and task management. Everything flows through CI/CD pipelines with automated testing and Docker deployments.",
    suggestions: [
      "Tell me about your testing approach",
      "What's your development workflow?",
      "How do you work with designers?",
    ],
  },

  // WORK EXPERIENCE OVERVIEW
  {
    keywords: [
      "experience",
      "worked",
      "companies",
      "jobs",
      "career",
      "background",
    ],
    patterns: [
      /work.*experience/i,
      /where.*worked/i,
      /previous.*jobs/i,
      /career/i,
    ],
    questionType: "what",
    category: "experience",
    answer:
      "I have 5+ years of full-stack software engineering experience. Currently, I'm at Progress Solutions building real-time data visualization platforms. Before that, I was a Frontend Engineer at SUNY New Paltz where I built React component libraries and migrated backends to Python FastAPI. Earlier, I was a Full-Stack Developer at Harns Technologies where I built 14 production applications serving 100K+ users. I also worked at QS Quaquarelli Symonds as a Frontend Developer on applications with 15M+ annual visitors, and started my career freelancing as a Full-Stack Developer building e-commerce platforms.",
    suggestions: [
      "Tell me about Progress Solutions",
      "What did you build at Harns?",
      "What about your education?",
    ],
  },

  // PROGRESS SOLUTIONS DETAILS
  {
    keywords: ["progress solutions", "progress", "current company"],
    patterns: [/progress.*solutions/i, /current.*company/i],
    questionType: "tell",
    category: "experience",
    answer:
      "At Progress Solutions, I work on full-stack development with a focus on real-time data visualization dashboards. I architect component libraries with React and Vue using TypeScript and Storybook documentation. One of my biggest wins was optimizing dashboard rendering from 2-3 seconds to under 500ms by switching from SVG to Canvas API. I build Python FastAPI and Node.js microservices with GraphQL, implement Redis caching, and work with Kafka for streaming 1M+ data points in real-time. Everything is deployed with Docker and Kubernetes on AWS, with CI/CD pipelines running Playwright and Jest tests achieving 90%+ coverage.",
    suggestions: [
      "How did you optimize performance?",
      "What's your tech stack?",
      "Tell me about other companies",
    ],
  },

  // SUNY EXPERIENCE
  {
    keywords: ["suny", "new paltz", "graduate assistant", "research assistant"],
    patterns: [/suny/i, /new.*paltz/i, /graduate.*assistant/i],
    questionType: "tell",
    category: "experience",
    answer:
      "At SUNY New Paltz, I was a Frontend Engineer building React and Vue component libraries with TypeScript and Storybook. I worked with Redux Toolkit and Pinia for state management, TanStack Query for data fetching, and created custom hooks and composables used across 4 university platforms. I led a backend migration from Node.js to Python FastAPI with MongoDB, implementing JWT authentication and Redis caching that cut API response times by 50%. I also set up CI/CD pipelines with Docker, Kubernetes on GCP, and automated testing with Jest and Playwright achieving 90%+ coverage. The work reduced UI development time by 30% and improved page load times by 45%.",
    suggestions: [
      "What technologies did you use?",
      "Tell me about your component libraries",
      "What about your other work?",
    ],
  },

  // HARNS EXPERIENCE
  {
    keywords: ["harns", "harns technologies"],
    patterns: [/harns/i],
    questionType: "tell",
    category: "experience",
    answer:
      "At Harns Technologies, I was a Full-Stack Developer building 14 production MERN applications serving 100K+ daily users. One highlight was a real-time IoT data visualization platform with Vue.js and D3.js frontend, Node.js backend with WebSocket connections processing data from 500+ IoT devices, and MongoDB time-series storage. I also built an e-commerce CMS with React frontend and Python Django/FastAPI backend handling 10K+ monthly transactions with Stripe and Razorpay integrations. I worked with Storybook for component documentation, established CI/CD pipelines with Docker and GitHub Actions, and achieved 90+ Lighthouse scores through performance optimization.",
    suggestions: [
      "What technologies did you use?",
      "Tell me about the IoT platform",
      "What about your other work?",
    ],
  },

  // EDUCATION
  {
    keywords: [
      "education",
      "degree",
      "university",
      "college",
      "studied",
      "school",
      "masters",
      "bachelor",
    ],
    patterns: [
      /education/i,
      /degree/i,
      /university/i,
      /studied/i,
      /graduated/i,
    ],
    questionType: "what",
    category: "education",
    answer:
      "I completed my Master's in Computer Science at SUNY New Paltz with a 3.81 GPA (2023-2025). Before that, I did my Bachelor's in Computer Science at Lovely Professional University with a 3.2 GPA (2017-2021). During my Master's, I worked as a Frontend Engineer building React and Vue component libraries, optimizing application performance, and migrating backends to Python FastAPI.",
    suggestions: [
      "What was your role at SUNY?",
      "What's your work experience?",
      "Tell me about your skills",
    ],
  },

  // PROJECTS
  {
    keywords: [
      "projects",
      "built",
      "created",
      "portfolio",
      "work samples",
      "made",
      "developed",
    ],
    patterns: [/what.*built/i, /projects/i, /portfolio/i, /work.*samples/i],
    questionType: "what",
    category: "projects",
    answer:
      "I've worked on a variety of full-stack projects:\n\n• Real-time data visualization dashboards using React/Vue with Plotly.js and D3.js processing 1M+ data points\n• Real-time IoT platform with Vue.js frontend and Node.js backend supporting 500+ devices\n• E-commerce CMS with React frontend and Python Django backend handling 10K+ monthly transactions\n• Component libraries with Storybook documentation used across multiple platforms\n• Multiple publishing platforms serving 50K+ daily readers\n• Applications with 15M+ annual visitors achieving 90+ Lighthouse scores\n\nAll built with modern tooling: TypeScript, Redux Toolkit/Pinia, TanStack Query, Docker, Kubernetes, CI/CD pipelines, and comprehensive testing with Jest, Playwright, and Cypress.",
    suggestions: [
      "Tell me about the IoT platform",
      "What technologies did you use?",
      "How did you optimize performance?",
    ],
  },

  // IOT PLATFORM DETAILS
  {
    keywords: ["iot", "sensors", "real-time", "devices"],
    patterns: [/iot/i, /sensors/i, /real.*time.*data/i],
    questionType: "tell",
    category: "projects",
    answer:
      "The IoT platform was a full-stack project. Frontend: Vue.js with D3.js for real-time visualizations (line charts, bar charts, heatmaps) all updating live. Backend: Node.js with WebSocket connections using Socket.io to stream data from 500+ devices with sub-second latency. We used MongoDB time-series collections for efficient data storage and Redis for caching frequently accessed data. The whole thing was deployed with Docker and handled Kafka streams for real-time event processing. It was a great project for working with real-time systems at scale.",
    suggestions: [
      "What visualization libraries did you use?",
      "Tell me about other projects",
      "What's your backend expertise?",
    ],
  },

  // PERFORMANCE WORK
  {
    keywords: [
      "performance",
      "optimization",
      "optimize",
      "fast",
      "speed",
      "improved",
    ],
    patterns: [/performance/i, /optimiz/i, /faster/i, /speed/i, /improve/i],
    questionType: "how",
    category: "skills",
    answer:
      "Performance optimization is something I really focus on. Some examples: I reduced dashboard rendering from 2-3 seconds to under 500ms by switching to Canvas API. I brought page load times down from 8 seconds to 2.5 seconds through code splitting with React.lazy() and bundle optimization. I cut API response times from 800ms to 200ms using Redis caching and PostgreSQL query optimization. I consistently achieve 90+ Lighthouse scores using React.memo, useMemo, useCallback, and lazy loading. I also reduced API calls by 45% using TanStack Query's intelligent caching strategies.",
    suggestions: [
      "How did you optimize rendering?",
      "What about database optimization?",
      "Tell me about your tech stack",
    ],
  },

  // TESTING
  {
    keywords: [
      "testing",
      "test",
      "quality",
      "jest",
      "cypress",
      "playwright",
      "coverage",
    ],
    patterns: [
      /testing/i,
      /test.*coverage/i,
      /quality/i,
      /jest/i,
      /cypress/i,
      /playwright/i,
    ],
    questionType: "what",
    category: "skills",
    answer:
      "I'm very serious about testing. I aim for 90%+ test coverage for critical features. I use Jest for unit testing, React Testing Library and Vue Test Utils for component testing, Cypress for integration tests, and Playwright for end-to-end testing. Everything runs through CI/CD pipelines with GitHub Actions, so issues are caught before they hit production. I also use Storybook for visual regression testing and component documentation. Testing isn't an afterthought — it's built into the development workflow from day one.",
    suggestions: [
      "What's your CI/CD experience?",
      "Tell me about your dev practices",
      "What tools do you use?",
    ],
  },

  // DATA VISUALIZATION
  {
    keywords: [
      "visualization",
      "charts",
      "graphs",
      "plotly",
      "d3",
      "dashboard",
    ],
    patterns: [/visualization/i, /charts/i, /plotly/i, /d3/i, /dashboard/i],
    questionType: "what",
    category: "skills",
    answer:
      "Data visualization is a major part of my work. I use Plotly.js for dashboards that handle 1M+ data points with smooth performance. D3.js for custom, interactive visualizations. And Canvas API when I need maximum rendering performance — that's how I got rendering times down to under 500ms. I build these visualizations with React and Vue, manage the data flow with TanStack Query or Redux Toolkit, and stream real-time updates using WebSockets and Kafka. Many of the dashboards I've worked on serve 50K+ users daily.",
    suggestions: [
      "How did you optimize performance?",
      "Tell me about your projects",
      "What's your tech stack?",
    ],
  },

  // BACKEND
  {
    keywords: [
      "backend",
      "api",
      "server",
      "node",
      "python",
      "fastapi",
      "django",
    ],
    patterns: [/backend/i, /api/i, /server/i, /node/i, /python/i, /fastapi/i],
    questionType: "what",
    category: "skills",
    answer:
      "On the backend, I work with Node.js and Express for JavaScript-based APIs, and Python with FastAPI and Django for Python-based services. I build both RESTful APIs and GraphQL endpoints using Apollo Server. For databases, I use PostgreSQL for relational data, MongoDB for document storage, and Redis for caching. I've optimized API response times to under 200ms through proper indexing, query optimization, and caching strategies. Everything is deployed with Docker and Kubernetes, with CI/CD pipelines for automated testing and deployment.",
    suggestions: [
      "What about frontend?",
      "Tell me about databases",
      "What projects did you build?",
    ],
  },

  // COMPONENT LIBRARIES
  {
    keywords: ["components", "component library", "reusable", "storybook"],
    patterns: [/component.*library/i, /reusable.*components/i, /storybook/i],
    questionType: "tell",
    category: "skills",
    answer:
      "I've built several component libraries across different projects. I use TypeScript for type safety, create custom hooks (React) and custom composables (Vue) for logic reusability, and document everything with Storybook. The libraries follow atomic design principles and include 40+ reusable components like buttons, forms, modals, cards, and complex data visualization components. These libraries have reduced development time by 30-40% across teams by providing consistent, well-tested, well-documented components. They're published as internal packages and used across multiple applications.",
    suggestions: [
      "What technologies do you use?",
      "Tell me about Storybook",
      "How do you ensure quality?",
    ],
  },

  // CI/CD AND DEVOPS
  {
    keywords: ["cicd", "ci/cd", "devops", "docker", "kubernetes", "deployment"],
    patterns: [/ci.*cd/i, /devops/i, /docker/i, /kubernetes/i, /deploy/i],
    questionType: "what",
    category: "skills",
    answer:
      "I work with CI/CD pipelines daily. I use GitHub Actions to automate testing (Jest, Playwright, Cypress), linting (ESLint), code coverage reporting, and deployments. Everything runs in Docker containers for consistency across environments. For production deployments, I use Kubernetes for orchestration and scaling on AWS (Lambda, S3, CloudFront) or GCP. The pipelines run automated tests on every PR, build Docker images, and deploy to staging/production environments. This setup has reduced deployment time by 65-70% and catches issues before they reach production.",
    suggestions: [
      "What's your testing strategy?",
      "Tell me about AWS experience",
      "What about Docker and Kubernetes?",
    ],
  },

  // AGILE AND COLLABORATION
  {
    keywords: ["agile", "scrum", "jira", "team", "collaboration", "figma"],
    patterns: [/agile/i, /scrum/i, /jira/i, /collaboration/i, /figma/i],
    questionType: "how",
    category: "skills",
    answer:
      "I work in Agile/Scrum environments using Jira for sprint planning, task management, and tracking. I collaborate closely with designers using Figma for design handoffs — reviewing designs, providing feedback on feasibility, and implementing pixel-perfect UIs. I participate in daily standups, sprint planning, retrospectives, and conduct code reviews with the team. I'm comfortable with pair programming and mentoring junior developers on React, Vue, TypeScript, and testing best practices. Cross-functional collaboration with product managers, designers, and backend engineers is a regular part of my workflow.",
    suggestions: [
      "What's your team experience?",
      "How do you work with designers?",
      "Tell me about code reviews",
    ],
  },

  // CONTACT
  {
    keywords: [
      "contact",
      "email",
      "phone",
      "reach",
      "connect",
      "linkedin",
      "github",
    ],
    patterns: [
      /contact/i,
      /email/i,
      /reach.*you/i,
      /connect/i,
      /linkedin/i,
      /github/i,
    ],
    questionType: "how",
    category: "contact",
    answer:
      "Sure! Here's how you can reach me:\n\nEmail: yerraguntlaharish98@gmail.com\nPhone: +1 (205) 736-5808\nLinkedIn: linkedin.com/in/harish-yerraguntla-70695416b\nGitHub: github.com/nandu064\nPortfolio: nandu064.github.io/portfolio\n\nI'm based in New York and open to remote opportunities. Work authorization valid through June 2028.",
    suggestions: [
      "Where are you located?",
      "What's your current role?",
      "Tell me about your experience",
    ],
  },

  // LOCATION
  {
    keywords: ["location", "where", "based", "live", "city", "remote"],
    patterns: [/where.*located/i, /where.*based/i, /where.*live/i, /location/i],
    questionType: "where",
    category: "contact",
    answer:
      "I'm currently based in Painted Post, New York. I work remotely for Progress Solutions in Texas. I'm authorized to work in the US through June 2028 under the OPT STEM extension. I'm open to remote opportunities and hybrid roles.",
    suggestions: [
      "What's your current role?",
      "How can I contact you?",
      "Tell me about your background",
    ],
  },

  // YEARS OF EXPERIENCE
  {
    keywords: ["how long", "years", "experience years", "how many years"],
    patterns: [/how.*long/i, /how.*many.*years/i, /years.*experience/i],
    questionType: "how",
    category: "experience",
    answer:
      "I have 5+ years of professional software engineering experience. I started freelancing as a full-stack developer in 2019, then worked at QS and Harns Technologies, completed my Master's in Computer Science, worked as a Frontend Engineer at SUNY New Paltz, and now I'm a full-time Software Developer at Progress Solutions.",
    suggestions: [
      "Where have you worked?",
      "What's your expertise?",
      "Tell me about your projects",
    ],
  },

  // GREETING
  {
    keywords: ["hello", "hi", "hey", "greetings"],
    patterns: [/^(hi|hello|hey|greetings)/i],
    questionType: "greeting",
    category: "general",
    answer:
      "Hey! I'm Harish. I'm a full-stack software engineer specializing in React, Vue, TypeScript, and Node.js. I love building real-time data visualization applications and solving complex performance problems. What would you like to know about me?",
    suggestions: [
      "What's your current role?",
      "What are your main skills?",
      "Tell me about your experience",
    ],
  },

  // WHO ARE YOU
  {
    keywords: ["who", "about", "yourself", "intro", "introduction"],
    patterns: [/who.*are.*you/i, /tell.*about.*yourself/i, /introduce/i],
    questionType: "who",
    category: "general",
    answer:
      "Sure! I'm Harish Yerraguntla, a full-stack software engineer with 5+ years of experience. I specialize in building scalable, high-performance web applications using React.js, Vue.js, TypeScript, and modern JavaScript frameworks on the frontend, and Node.js and Python (FastAPI/Django) on the backend. I've worked on applications serving 2M+ users and love performance optimization, real-time data visualization, and building reusable component architectures. I recently finished my Master's in Computer Science from SUNY New Paltz with a 3.81 GPA.",
    suggestions: [
      "What's your current project?",
      "What technologies do you use?",
      "Tell me about your achievements",
    ],
  },
];
